import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { ArrowLeft, ArrowRight, Clock3, Video, User, Sparkles } from "lucide-react";

// Minimal, safe markdown → JSX renderer for our controlled article content
// (headings, bold, tables, lists, paragraphs). We intentionally don't pull a
// full markdown library — the input is server-controlled static content.
function renderMd(md) {
  const lines = (md || "").split("\n");
  const out = [];
  let i = 0;
  const key = () => `k-${out.length}`;

  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-[#F1F3F7] text-[#7C3AED] font-mono text-[0.9em]">$1</code>');

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();

    // Skip blank lines
    if (!line.trim()) { i++; continue; }

    // Heading (### only, matches our article style)
    if (line.startsWith("### ")) {
      out.push(<h3 key={key()} className="font-display font-black text-2xl sm:text-3xl mt-10 mb-3 tracking-tight text-[#0B0D17]">{line.slice(4)}</h3>);
      i++; continue;
    }

    // Fenced code block ``` (used for DNS records etc.)
    if (line.startsWith("```")) {
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) { buf.push(lines[i]); i++; }
      i++; // consume closing ```
      out.push(
        <pre key={key()} className="my-5 rounded-xl bg-[#0B0D17] text-[#22D3EE] p-4 overflow-x-auto text-xs font-mono leading-relaxed">
          <code>{buf.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Table (starts with |)
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(lines[i].trim()); i++; }
      // parse: header | separator | body...
      const parseRow = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const header = parseRow(rows[0]);
      const body = rows.slice(2).map(parseRow);
      out.push(
        <div key={key()} className="my-6 overflow-x-auto vp-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F6F7FA]">
                {header.map((h, hi) => (
                  <th key={hi} className="text-left p-3 text-[11px] uppercase tracking-widest text-[#4A4D57] font-semibold border-b border-[#E4E6EC]"
                    dangerouslySetInnerHTML={{ __html: inline(h) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri} className="border-t border-[#E4E6EC]">
                  {r.map((c, ci) => (
                    <td key={ci} className="p-3 text-[#0B0D17]" dangerouslySetInnerHTML={{ __html: inline(c) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      out.push(
        <ul key={key()} className="my-4 space-y-2 pl-5 list-disc marker:text-[#0B5CFF] text-[#0B0D17]">
          {items.map((it, ii) => (
            <li key={ii} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ul>
      );
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      const buf = [line.slice(2)];
      i++;
      while (i < lines.length && lines[i].startsWith("> ")) { buf.push(lines[i].slice(2)); i++; }
      out.push(
        <blockquote key={key()} className="my-5 pl-4 border-l-4 border-[#0B5CFF] text-[#0B0D17] italic leading-relaxed">
          <span dangerouslySetInnerHTML={{ __html: inline(buf.join(" ")) }} />
        </blockquote>
      );
      continue;
    }

    // Paragraph (accumulate until blank line)
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^([#\->\|`]|- )/.test(lines[i].trim())) {
      buf.push(lines[i].trim());
      i++;
    }
    out.push(
      <p key={key()} className="my-4 text-[#0B0D17] leading-relaxed text-[17px]"
        dangerouslySetInnerHTML={{ __html: inline(buf.join(" ")) }} />
    );
  }
  return out;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/blog/${slug}`);
        setData(data);
        document.title = `${data.article.title} · Videoepreneur Blog`;
      } catch (e) { setErr(e.response?.data?.detail || "Article not found"); }
    })();
    window.scrollTo(0, 0);
  }, [slug]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="vp-card p-8 max-w-md text-center">
        <div className="vp-label mb-2">Blog</div>
        <div className="font-display text-2xl font-medium">Article nahi mila.</div>
        <p className="text-[#4A4D57] mt-2 text-sm">{err}</p>
        <Link to="/blog" className="vp-btn-ghost mt-6 inline-flex">← Back to blog</Link>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  const { article, related } = data;

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="max-w-[1280px] mx-auto flex items-center justify-between px-5 sm:px-8 py-6">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center">
            <Video className="w-5 h-5 text-white" strokeWidth={1.75} />
          </div>
          <span className="font-display font-semibold text-lg tracking-tight">Videoepreneur</span>
        </Link>
        <Link to="/signup" className="vp-btn-primary text-sm">Free me shuru karo</Link>
      </nav>

      {/* Hero */}
      <header className="max-w-3xl mx-auto px-5 sm:px-8 pt-6 pb-10">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[#4A4D57] hover:text-[#0B5CFF]" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
        <div className="mt-6 flex items-center gap-3 flex-wrap text-xs">
          <span className="px-2.5 py-1 rounded-full bg-[#0B5CFF]/10 text-[#0B5CFF] font-semibold">{article.category}</span>
          <span className="inline-flex items-center gap-1 text-[#747680]"><Clock3 className="w-3 h-3" /> {article.read_min} min read</span>
          <span className="text-[#747680]">· {new Date(article.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05] tracking-tight text-[#0B0D17]" data-testid="blog-article-title">
          {article.title}
        </h1>
        <p className="text-lg text-[#4A4D57] mt-5 leading-relaxed">{article.excerpt}</p>
        <div className="mt-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0B5CFF] to-[#7C3AED] flex items-center justify-center text-white font-bold text-sm">
            {article.author.split(" ").map((x) => x[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="font-semibold text-sm text-[#0B0D17]">{article.author}</div>
            <div className="text-xs text-[#747680] inline-flex items-center gap-1"><User className="w-3 h-3" /> Videoepreneur team</div>
          </div>
        </div>
      </header>

      {/* Hero band */}
      <div
        className="max-w-4xl mx-auto rounded-2xl h-40 sm:h-56 mb-10 relative overflow-hidden"
        style={{ background: article.hero_gradient }}
      >
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), transparent 55%)" }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <Sparkles className="w-12 h-12 text-white/70" strokeWidth={1.5} />
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pb-16" data-testid="blog-article-body">
        {renderMd(article.body_md)}
      </article>

      {/* Inline CTA */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
        <div className="vp-card p-6 sm:p-8 bg-gradient-to-br from-[#0B5CFF]/8 to-[#7C3AED]/8 border-[#0B5CFF]/40 text-center">
          <div className="font-display font-black text-2xl">Ready to try?</div>
          <p className="text-[#4A4D57] mt-2 max-w-md mx-auto">Free plan me unlimited webinars host karo. Card nahi chahiye. Pehla webinar 40 sec me publish ho jaayega.</p>
          <Link to="/signup" className="mt-5 vp-btn-primary text-sm inline-flex items-center gap-2" data-testid="blog-inline-cta">
            Free me shuru karo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Related */}
      {related?.length > 0 && (
        <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24">
          <div className="vp-label mb-4">Related reading</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`blog-related-${a.slug}`}
                className="group vp-card overflow-hidden hover:-translate-y-0.5 transition-all"
              >
                <div className="aspect-[16/10]" style={{ background: a.hero_gradient }} />
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#0B5CFF] font-bold">{a.category}</div>
                  <div className="font-display font-bold text-sm mt-1.5 line-clamp-2 leading-snug text-[#0B0D17] group-hover:text-[#0B5CFF] transition-colors">
                    {a.title}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
